/**
 * Aggregate save orchestration (#1501 Option B, client side).
 *
 * Builds ONE POST /api/requests/:id/save payload from the parent dirty fields,
 * the team rows, and the six family bridges' pending diffs (via the pure
 * compute functions the flushes share), sends it, and absorbs the response
 * back into the bridges:
 *
 * - full success → every sent family absorbs its idMap-normalized rows (the
 *   exact post-flush reset the legacy path performs);
 * - 207 partial → families that fully applied absorb; the failed family only
 *   remaps its applied temp ids (stays dirty, retry re-diffs honestly);
 *   skipped families stay untouched.
 * - 404 AGGREGATE_SAVE_DISABLED → 'unsupported': the caller runs the legacy
 *   per-section sequence instead (server/client flags flip independently).
 */

import { ApiError } from './api';
import {
  hasOrderChange,
  type ComputedRepeatableOps,
} from './computeRepeatableOperations';
import { computeCoolingSystemOperations } from './coolingSystemEditSessionFlush';
import { toCreateCoolingSystemPayload } from './coolingSystemPayload';
import { computeLargeUpsOperations } from './largeUpsEditSessionFlush';
import { toCreateLargeUpsPayload } from './largeUpsPayload';
import { computeLiquidCoolingCduOperations } from './liquidCoolingCduEditSessionFlush';
import { toCreateLiquidCoolingCduPayload } from './liquidCoolingCduPayload';
import { computePowerDistributionOperations } from './powerDistributionEditSessionFlush';
import { toCreatePowerDistributionPayload } from './powerDistributionPayload';
import { computeRackOperations } from './rackEditSessionFlush';
import { toCreateRackPayload } from './rackPayload';
import { RequestsService } from './requestsService';
import { computeSmallUpsOperations } from './smallUpsEditSessionFlush';
import { toCreateSmallUpsPayload } from './smallUpsPayload';
import {
  AGGREGATE_SAVE_DISABLED_CODE,
  type AggregateFamilyKey,
  type AggregateFamilyOps,
  type AggregateSavePayload,
  type AggregateSaveResult,
} from '../types/aggregateSave';

import type { AggregateSaveBridgeExtras } from '../hooks/useRepeatableEngineSaveBridge';
import type { TeamInformation } from '../types';

type AnyRow = { id?: number; requestId?: number | string } & Record<
  string,
  unknown
>;

interface FamilyWiring {
  key: AggregateFamilyKey;
  compute: (
    baseline: AnyRow[],
    session: AnyRow[],
  ) => ComputedRepeatableOps<AnyRow>;
  toCreatePayload: (row: AnyRow) => Record<string, unknown>;
}

const FAMILY_WIRINGS: FamilyWiring[] = [
  {
    key: 'racks',
    compute: computeRackOperations as unknown as FamilyWiring['compute'],
    toCreatePayload:
      toCreateRackPayload as unknown as FamilyWiring['toCreatePayload'],
  },
  {
    key: 'smallUps',
    compute: computeSmallUpsOperations as unknown as FamilyWiring['compute'],
    toCreatePayload:
      toCreateSmallUpsPayload as unknown as FamilyWiring['toCreatePayload'],
  },
  {
    key: 'largeUps',
    compute: computeLargeUpsOperations as unknown as FamilyWiring['compute'],
    toCreatePayload:
      toCreateLargeUpsPayload as unknown as FamilyWiring['toCreatePayload'],
  },
  {
    key: 'coolingSystems',
    compute:
      computeCoolingSystemOperations as unknown as FamilyWiring['compute'],
    toCreatePayload:
      toCreateCoolingSystemPayload as unknown as FamilyWiring['toCreatePayload'],
  },
  {
    key: 'liquidCoolingCdus',
    compute:
      computeLiquidCoolingCduOperations as unknown as FamilyWiring['compute'],
    toCreatePayload:
      toCreateLiquidCoolingCduPayload as unknown as FamilyWiring['toCreatePayload'],
  },
  {
    key: 'powerDistribution',
    compute:
      computePowerDistributionOperations as unknown as FamilyWiring['compute'],
    toCreatePayload:
      toCreatePowerDistributionPayload as unknown as FamilyWiring['toCreatePayload'],
  },
];

export type AggregateFamilyBridges = Partial<
  Record<AggregateFamilyKey, AggregateSaveBridgeExtras | null>
>;

export interface AggregateSaveAttemptInputs {
  requestId: string;
  /** Parent dirty fields (requestDataWithoutTeam); empty object = no segment. */
  parentData: Record<string, unknown>;
  /** Deduplicated team rows; null = no team segment this Save. */
  teamRows: TeamInformation[] | null;
  bridges: AggregateFamilyBridges;
}

export interface AggregateSaveAttemptOutcome {
  kind: 'saved' | 'nothing-to-send' | 'unsupported';
  result?: AggregateSaveResult;
  /** Per-family temp→real id maps (for staged-upload key remapping). */
  createdIdMapsByFamily: Map<AggregateFamilyKey, Map<number, number>>;
}

interface SentFamily {
  key: AggregateFamilyKey;
  bridge: AggregateSaveBridgeExtras;
  session: AnyRow[];
  ops: ComputedRepeatableOps<AnyRow>;
}

export function isAggregateSaveDisabledError(error: unknown): boolean {
  // Duck-typed rather than `instanceof ApiError`: the error may cross module
  // registries (jest projects, dual bundles) where instanceof lies.
  if (typeof error !== 'object' || error === null) {
    return false;
  }
  const e = error as { status?: unknown; code?: unknown };
  return e.status === 404 && e.code === AGGREGATE_SAVE_DISABLED_CODE;
}

export async function attemptAggregateSave(
  inputs: AggregateSaveAttemptInputs,
): Promise<AggregateSaveAttemptOutcome> {
  const { requestId, parentData, teamRows, bridges } = inputs;

  const payload: AggregateSavePayload = {};
  if (Object.keys(parentData).length > 0) {
    payload.request = parentData;
  }
  if (teamRows !== null) {
    payload.team = teamRows;
  }

  const sentFamilies: SentFamily[] = [];
  for (const wiring of FAMILY_WIRINGS) {
    const bridge = bridges[wiring.key];
    const diff = bridge?.getPendingDiff();
    if (!bridge || !diff) {
      continue;
    }
    const baseline = diff.baseline as AnyRow[];
    const session = diff.session as AnyRow[];
    const ops = wiring.compute(baseline, session);

    // clientKeys are namespaced per family on the wire — temp ids are only
    // unique within a family, and the server's idMap is one flat record.
    const wireKey = (clientKey: string): string => `${wiring.key}:${clientKey}`;

    const familyOps: AggregateFamilyOps = {};
    if (ops.creates.length > 0) {
      familyOps.create = ops.creates.map(create => ({
        ...wiring.toCreatePayload({
          ...create.row,
          requestId: parseInt(requestId, 10) || create.row.requestId,
        }),
        clientKey: wireKey(create.clientKey),
      }));
    }
    if (ops.updates.length > 0) {
      familyOps.update = ops.updates.map(update => ({
        id: update.id,
        ...update.data,
      }));
    }
    if (ops.deletes.length > 0) {
      familyOps.delete = ops.deletes;
    }
    if (hasOrderChange(ops)) {
      familyOps.reorder = ops.orderRefs.map(ref =>
        typeof ref === 'string' ? wireKey(ref) : ref,
      );
    }
    if (Object.keys(familyOps).length === 0) {
      continue;
    }
    payload[wiring.key] = familyOps;
    sentFamilies.push({ key: wiring.key, bridge, session, ops });
  }

  if (Object.keys(payload).length === 0) {
    return { kind: 'nothing-to-send', createdIdMapsByFamily: new Map() };
  }

  let result: AggregateSaveResult;
  try {
    result = await RequestsService.saveAggregate(requestId, payload);
  } catch (error) {
    if (isAggregateSaveDisabledError(error)) {
      return { kind: 'unsupported', createdIdMapsByFamily: new Map() };
    }
    throw error;
  }

  const createdIdMapsByFamily = new Map<
    AggregateFamilyKey,
    Map<number, number>
  >();

  for (const sent of sentFamilies) {
    // temp id → real id, from the server's (family-namespaced) clientKey map
    const idMap = new Map<number, number>();
    for (const create of sent.ops.creates) {
      const realId = result.idMap[`${sent.key}:${create.clientKey}`];
      if (create.tempId != null && realId != null) {
        idMap.set(create.tempId, realId);
      }
    }
    if (idMap.size > 0) {
      createdIdMapsByFamily.set(sent.key, idMap);
    }

    const prefix = `${sent.key}.`;
    const familyFailed = result.failed?.section === sent.key;
    const familySkipped =
      result.skipped.some(label => label.startsWith(prefix)) || familyFailed;

    if (!familySkipped) {
      // Fully applied: the same post-flush reset the legacy flush performs.
      const normalized = sent.session.map(row =>
        row.id != null && idMap.has(row.id)
          ? { ...row, id: idMap.get(row.id)! }
          : row,
      );
      await sent.bridge.absorbExternalFlushResult(normalized);
    } else if (idMap.size > 0) {
      // Partially applied: keep the family dirty, but swap applied temp ids so
      // the retry's re-diff skips the already-created units (their content is
      // persisted; created rows absent from the baseline are never re-sent).
      sent.bridge.remapSessionIds(idMap);
    }
  }

  return { kind: 'saved', result, createdIdMapsByFamily };
}
